import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { addAppealPreJudgment } from "services/appealService";
import {
  AppealPreJudgement,
  AppealPreJudgementDecision,
  AppealRequest,
  DefendantAppeal,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  appealData?: AppealRequest;
  defendantAppealData?: DefendantAppeal;
  inline?: boolean;
  onClose: (refetch: boolean) => void;
};

const AppealPreJudgmentForm: FC<Props> = ({
  appealData,
  defendantAppealData,
  inline,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<AppealPreJudgement>({
    appeal_request: appealData?.id,
    defendant_appeal_request: defendantAppealData?.id,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...(prevState ?? {}),
      [name]: name === "decision_date" ? value : value,
    }));
  };

  const validateForm = () => {
    if (!formState?.decision_date) {
      return t("decision_date_required");
    }
    if (!formState?.summary) {
      return t("summary_required");
    }
    if (!formState?.decision) {
      return t("decision_required");
    }
    if (!formState?.remarks) {
      return t("remarks_required");
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }
    setLoading(true);

    addAppealPreJudgment(formState)
      .then(() => {
        showTooltip(t("successfully_added"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={inline ? undefined : { marginTop: "0px" }}
      className={
        inline
          ? "w-full"
          : "fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      }
      onClick={inline ? undefined : handleClickOutside}
    >
      <div
        className={
          inline
            ? "relative flex w-full flex-col rounded-xl bg-white border border-gray-200 shadow-sm"
            : "relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl"
        }
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1.5 rounded-full bg-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t("appeal_pre_judgement_form")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("pre_judgement_form_desc", {
                  defaultValue:
                    "Record the court's pre-judgement decision, date, and summary.",
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("decision_date")}
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="decision_date"
                value={
                  formState?.decision_date
                    ? new Date(formState.decision_date)
                        .toISOString()
                        .substring(0, 10)
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("summary")}
              </label>
              <textarea
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="summary"
                value={formState?.summary ?? ""}
                onChange={handleChange}
                rows={4}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("decision")}
              </label>
              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="decision"
                value={formState?.decision ?? ""}
                onChange={handleChange}
              >
                <option value="">{t("select_decision")}</option>
                {Object.entries(AppealPreJudgementDecision).map(
                  ([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ),
                )}
              </select>
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("remarks")}
              </label>
              <textarea
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="remarks"
                value={formState?.remarks ?? ""}
                onChange={handleChange}
                rows={4}
              />
            </div>
          </div>
            <div className="flex justify-end gap-3 mt-2 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    {LoadingIcon}
                    {t("loading")}
                  </>
                ) : (
                  t("add")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppealPreJudgmentForm;
